import { addDays, format, parseISO, startOfWeek } from 'date-fns';

export const TRACKER_METRICS = [
  { key: 'total_called', label: 'People Called', type: 'auto' },
  { key: 'pickups', label: 'Pick Ups', type: 'auto' },
  { key: 'thirty_sec_convos', label: '30 Sec+ Convos', type: 'auto' },
  { key: 'two_min_convos', label: '2 Min+ Convos', type: 'auto' },
  { key: 'presentations', label: 'Presentations', type: 'manual' },
  { key: 'appts_set', label: 'New Appts Set', type: 'auto' },
  { key: 'appts_on_calendar', label: 'Appts On Calendar', type: 'auto' },
  { key: 'appts_showed', label: 'Appts Showed', type: 'auto' },
  { key: 'apps_submitted', label: 'Apps Submitted', type: 'manual' },
  { key: 'ap_submitted_dollars', label: '$AP Submitted', type: 'manual', isDollar: true },
  { key: 'referrals_gathered', label: 'Referrals Gathered', type: 'manual' },
  { key: 'objections_received', label: 'Objections Received', type: 'manual' },
];

const MANUAL_KEYS = [
  'presentations',
  'apps_submitted',
  'ap_submitted_dollars',
  'referrals_gathered',
  'objections_received',
];

export function getMondayOfWeek(date = new Date()) {
  const monday = startOfWeek(date, { weekStartsOn: 1 });
  return format(monday, 'yyyy-MM-dd');
}

export function addWeeksToMonday(weekStartIso, weeks) {
  const d = parseISO(`${weekStartIso}T12:00:00`);
  return format(addDays(d, weeks * 7), 'yyyy-MM-dd');
}

export function getWeekDayStrings(weekStartDate) {
  const start = parseISO(`${weekStartDate}T12:00:00`);
  return Array.from({ length: 7 }, (_, i) => format(addDays(start, i), 'yyyy-MM-dd'));
}

export function formatWeekRangeLabel(weekStartDate) {
  const start = parseISO(`${weekStartDate}T12:00:00`);
  const end = addDays(start, 6);
  return `${format(start, 'MMM d')}–${format(end, 'd, yyyy')}`;
}

export function todayDateKey() {
  return format(new Date(), 'yyyy-MM-dd');
}

export function isFutureDate(dateKey) {
  return dateKey > todayDateKey();
}

export function buildManualByDay(logs, { sumByDate = false } = {}) {
  const byDay = {};
  for (const row of logs ?? []) {
    const d = row.date;
    if (!byDay[d]) {
      byDay[d] = emptyManualDay();
    }
    if (sumByDate) {
      for (const key of MANUAL_KEYS) {
        byDay[d][key] += Number(row[key]) || 0;
      }
    } else {
      byDay[d] = {
        presentations: Number(row.presentations) || 0,
        apps_submitted: Number(row.apps_submitted) || 0,
        ap_submitted_dollars: Number(row.ap_submitted_dollars) || 0,
        referrals_gathered: Number(row.referrals_gathered) || 0,
        objections_received: Number(row.objections_received) || 0,
      };
    }
  }
  return byDay;
}

export function emptyManualDay() {
  return {
    presentations: 0,
    apps_submitted: 0,
    ap_submitted_dollars: 0,
    referrals_gathered: 0,
    objections_received: 0,
  };
}

const ACTIVITY_MANUAL_METRIC_KEYS = new Set([
  'presentations',
  'apps_submitted',
  'ap_submitted_dollars',
  'referrals_gathered',
  'objections_received',
]);

export function getDayValue(metricKey, day, autoByDay, manualByDay, isFuture) {
  if (isFuture) {
    const m = TRACKER_METRICS.find((x) => x.key === metricKey);
    return m?.type === 'auto' ? 0 : 0;
  }
  const m = TRACKER_METRICS.find((x) => x.key === metricKey);
  if (!m) return 0;
  if (m.type === 'auto') {
    let val = Number(autoByDay?.[day]?.[metricKey]) || 0;
    if (metricKey === 'total_called') {
      val += Number(autoByDay?.[day]?.manual_calls) || 0;
    }
    return val;
  }
  const manual = Number(manualByDay?.[day]?.[metricKey]) || 0;
  if (ACTIVITY_MANUAL_METRIC_KEYS.has(metricKey)) {
    return manual + (Number(autoByDay?.[day]?.[metricKey]) || 0);
  }
  return manual;
}

/** Manual-call activity logs counted toward People Called (from activity_logs). */
export function getActivityManualCallsForDay(day, autoByDay) {
  return Number(autoByDay?.[day]?.manual_calls) || 0;
}

export function sumWeekTotals(dayStrings, autoByDay, manualByDay) {
  const totals = {};
  for (const m of TRACKER_METRICS) {
    totals[m.key] = 0;
  }
  for (const day of dayStrings) {
    const future = isFutureDate(day);
    for (const m of TRACKER_METRICS) {
      totals[m.key] += getDayValue(m.key, day, autoByDay, manualByDay, future);
    }
  }
  return totals;
}

export function countDaysWithCalls(dayStrings, autoByDay) {
  let n = 0;
  for (const day of dayStrings) {
    if (isFutureDate(day)) continue;
    if ((Number(autoByDay?.[day]?.total_called) || 0) > 0) n += 1;
  }
  return n;
}

export function pctRatio(numerator, denominator) {
  if (!denominator) return '—';
  return `${((numerator / denominator) * 100).toFixed(1)}%`;
}

export function formatDollars(amount) {
  if (!amount) return '$0';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function computeTrackerRatios(totals, daysWithCalls) {
  return [
    { label: 'Pick Up Ratio', value: pctRatio(totals.pickups, totals.total_called) },
    { label: '30 Sec+ Ratio', value: pctRatio(totals.thirty_sec_convos, totals.pickups) },
    { label: '2 Min+ Ratio', value: pctRatio(totals.two_min_convos, totals.pickups) },
    { label: 'Presentation Ratio', value: pctRatio(totals.presentations, totals.pickups) },
    { label: 'New Appts Ratio', value: pctRatio(totals.appts_set, totals.presentations) },
    { label: 'Appt Show Ratio', value: pctRatio(totals.appts_showed, totals.appts_on_calendar) },
    { label: 'Closing Ratio (Pickups)', value: pctRatio(totals.apps_submitted, totals.pickups) },
    { label: 'Closing Ratio (Pres)', value: pctRatio(totals.apps_submitted, totals.presentations) },
    {
      label: 'Avg Apps / Day',
      value: daysWithCalls
        ? (totals.apps_submitted / daysWithCalls).toFixed(2)
        : '—',
    },
    {
      label: 'Avg $AP / App',
      value: totals.apps_submitted
        ? formatDollars(totals.ap_submitted_dollars / totals.apps_submitted)
        : '—',
    },
    {
      label: 'Avg Referrals',
      value: daysWithCalls
        ? (totals.referrals_gathered / daysWithCalls).toFixed(2)
        : '—',
    },
  ];
}

export function manualPayloadFromDay(manualByDay, day, { channel = null } = {}) {
  return {
    ...(manualByDay[day] ?? emptyManualDay()),
    ...(channel ? { channel } : {}),
  };
}

export { MANUAL_KEYS };
